import { Clock, AlertTriangle, XCircle, CheckCircle } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { DLC_CRITICAL_DAYS, DLC_WARNING_DAYS } from '@/lib/constants';
import type { DLCUrgence } from '@/types/inventory.types';

interface DLCBadgeProps {
  dlc: string | null;
  joursRestants?: number;
  urgence?: DLCUrgence;
  showDate?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const urgenceConfig: Record<DLCUrgence, {
  variant: 'success' | 'warning' | 'danger' | 'info' | 'neutral';
  icon: typeof Clock;
  label: string;
}> = {
  ok: {
    variant: 'success',
    icon: CheckCircle,
    label: 'OK',
  },
  attention: {
    variant: 'warning',
    icon: Clock,
    label: 'Bientôt',
  },
  critique: {
    variant: 'danger',
    icon: AlertTriangle,
    label: 'Urgent',
  },
  expire: {
    variant: 'danger',
    icon: XCircle,
    label: 'Périmé',
  },
};

function calculateUrgence(dlc: string | null): { urgence: DLCUrgence; jours: number } {
  if (!dlc) return { urgence: 'ok', jours: Infinity };

  const dlcDate = new Date(dlc);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffTime = dlcDate.getTime() - today.getTime();
  const jours = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (jours <= 0) return { urgence: 'expire', jours };
  if (jours <= DLC_CRITICAL_DAYS) return { urgence: 'critique', jours };
  if (jours <= DLC_WARNING_DAYS) return { urgence: 'attention', jours };
  return { urgence: 'ok', jours };
}

export function DLCBadge({
  dlc,
  joursRestants: propJours,
  urgence: propUrgence,
  showDate = false,
  size = 'md',
}: DLCBadgeProps) {
  // Si pas de DLC, ne rien afficher
  if (!dlc) return null;

  // Calculer ou utiliser les props
  const { urgence: calcUrgence, jours: calcJours } = calculateUrgence(dlc);
  const urgence = propUrgence ?? calcUrgence;
  const jours = propJours ?? calcJours;

  const config = urgenceConfig[urgence];
  const Icon = config.icon;

  // Formater la date
  const formattedDate = new Date(dlc).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
  });

  // Texte du badge
  let text: string;
  if (urgence === 'expire') {
    text = jours === 0 ? "Expire aujourd'hui" : `Périmé (${Math.abs(jours)}j)`;
  } else if (jours === 1) {
    text = 'Demain';
  } else if (jours <= 7) {
    text = `${jours} jours`;
  } else {
    text = showDate ? formattedDate : `${jours}j`;
  }

  const sizeClasses = {
    sm: 'text-xs px-1.5 py-0.5',
    md: 'text-sm px-2 py-1',
    lg: 'text-base px-3 py-1.5',
  };

  const iconSizes = {
    sm: 12,
    md: 14,
    lg: 16,
  };

  return (
    <Badge
      variant={config.variant}
      className={`inline-flex items-center gap-1 ${sizeClasses[size]}`}
    >
      <Icon size={iconSizes[size]} />
      <span>{text}</span>
      {showDate && jours <= 7 && (
        <span className="opacity-70 ml-1">({formattedDate})</span>
      )}
    </Badge>
  );
}

// Composant simple pour afficher juste l'indicateur de couleur
export function DLCIndicator({ dlc, className = '' }: { dlc: string | null; className?: string }) {
  if (!dlc) return null;

  const { urgence } = calculateUrgence(dlc);

  const colors: Record<DLCUrgence, string> = {
    ok: 'bg-green-500',
    attention: 'bg-yellow-500',
    critique: 'bg-orange-500',
    expire: 'bg-red-500',
  };

  return (
    <span
      className={`inline-block w-2 h-2 rounded-full ${colors[urgence]} ${className}`}
      title={`DLC: ${new Date(dlc).toLocaleDateString('fr-FR')}`}
    />
  );
}
